import { AxiosInstance } from 'axios';

/**
 * Draining a poison queue: putting the failed messages back on the queue they broke on.
 *
 * This is not the same as requeue.ts. That one rebuilds a queue message from a log row,
 * which is the only option left when the message itself is gone. This one moves the
 * original messages, contents untouched, back to the step they came from.
 *
 * This client is kept apart from api.ts because that file is generated by nswag and gets
 * overwritten on the next generation. The axios instance was created with
 * transformResponse: [], so response.data is a string and has to be parsed here.
 */

export interface PoisonRequeueItem {
    messageId: string;
    body?: string;
    objectType?: string;
    relationId?: string;
    insertedOn?: string;
    moved: boolean;
    error?: string;
}

export interface PoisonRequeueResult {
    poisonQueue: string;
    targetQueue: string;
    poisonDepthBefore: number;
    /** Number of messages read from the poison queue. */
    read: number;
    /** Number that is now on the target queue and gone from the poison queue. */
    moved: number;
    /** Not moved, so these are still in the poison queue. */
    failed: number;
    /** Left in the poison queue on purpose: offering them again cannot succeed. */
    blocked?: number;
    /** Moved but not deleted, so this one gets delivered twice. */
    duplicated: number;
    poisonDepthAfter: number;
    targetDepthAfter: number;
    dryRun: boolean;
    /** The ceiling was reached; there is more waiting. */
    moreLeft: boolean;
    error?: string;
    summary: string;
    sample: PoisonRequeueItem[];
}

export interface PoisonRequeueOptions {
    /** How many messages may move at most. Empty means all of them. */
    max?: number;
    /** Only report what would happen; touches neither queue. */
    dryRun?: boolean;
    /** A target queue other than the one the poison queue belongs to. */
    targetQueue?: string;
}

const parse = <T,>(data: any): T =>
    typeof data === 'string' ? (JSON.parse(data) as T) : (data as T);

/** A response body that turns out to be a result rather than an error. */
const asResult = (data: any): PoisonRequeueResult | null => {
    try {
        const parsed = parse<PoisonRequeueResult>(data);

        return parsed && typeof parsed.summary === 'string' ? parsed : null;
    } catch {
        return null;
    }
};

/**
 * Put the contents of a poison queue back on the queue it belongs to.
 *
 * The API answers with a 400 when nothing could be moved, but still puts the result in the
 * body: that explanation is exactly what you want to see. Which is why such a response
 * comes back here as an ordinary result instead of as an exception.
 */
export const requeuePoisonQueue = async (
    axios: AxiosInstance,
    customer: string,
    environment: string,
    poisonQueue: string,
    options: PoisonRequeueOptions = {}
): Promise<PoisonRequeueResult> => {
    try {
        const response = await axios.post(
            `/QueueLog/${encodeURIComponent(customer)}/${encodeURIComponent(
                environment
            )}/queues/${encodeURIComponent(poisonQueue)}/requeue`,
            {
                max: options.max,
                dryRun: options.dryRun ?? false,
                targetQueue: options.targetQueue,
            }
        );

        return parse<PoisonRequeueResult>(response.data);
    } catch (error: any) {
        const result = asResult(error?.response?.data);

        if (result) {
            return result;
        }

        throw error;
    }
};
